<template>
  <div :class="$style.inlineContainer">
    <HgButton
      theme="secondary"
      icon="Plus"
      :class="$style.button"
      @click="handleClick(canUserCreate)"
    >
      {{ t('propertyDetail.property.addNewProperty') }}
    </HgButton>
    <HgToast
      :isVisible="showLimitMessage"
      :position="ToastPosition.TopCenter"
      :width="BREAKPOINTS.funnel_container_max_width.toString() + 'px'"
      :class="$style.toastNotificationContainer"
      :keepVisible="true"
    >
      <FormNotification
        :isNotificationOpen="showLimitMessage"
        :title="t('propertyDetail.property.propertyCreationLimitTitle')"
        :message="t('propertyDetail.property.propertyCreationLimitTooltip')"
        theme="warning"
        @close="showLimitMessage = false"
      >
        <i18n-t keypath="propertyDetail.property.propertyCreationLimitTooltip" tag="span">
          <template #businessPackageLink>
            <HgLink
              :href="t('propertyDetail.property.propertyCreationLimitLink')"
              target="_blank"
              @click="handleGoToPackagesClick"
            >
              {{ t('propertyDetail.property.propertyCreationLimitLinkText') }}
            </HgLink>
          </template>
        </i18n-t>
      </FormNotification>
    </HgToast>
  </div>
</template>
<script setup lang="ts">
import { FormNotification } from '@/components/common';
import { VALUATION_FORM_PATH } from '@/router/routes/constants';
import {
  trackingClickEvent,
  trackingFeatureDisplayEvent,
  trackingGenericClickEvent,
} from '@/tracking/tracking';
import {
  LOCAL_LANDING_PAGE_CATEGORY,
  LOCAL_LANDING_PAGE_EVENT,
  TRACKING_ACTION,
  TRACKING_LABEL,
} from '@/tracking/types';
import { BREAKPOINTS } from '@/types';
import { HgButton, HgLink, HgToast, ToastPosition } from '@smg-real-estate/domus';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

const { t, locale } = useI18n();
const { push } = useRouter();
const store = useStore();

const showLimitMessage = ref(false);
const canUserCreate = computed(() => store.getters['common/canCreateProperty']);

const handleGoToPackagesClick = () => {
  trackingGenericClickEvent(
    {
      event: LOCAL_LANDING_PAGE_EVENT.LINK_CLICK,
      category: LOCAL_LANDING_PAGE_CATEGORY.LINK_CLICK,
      action: TRACKING_ACTION.OPEN_BUSINESS_PACKAGE,
      label: TRACKING_LABEL.VALUATION_FUNNEL_TOP_BUTTON,
    },
    {
      h_extra_information_144: 'property_limit_reached',
    },
  );
};

const handleClick = (canCreate: boolean) => {
  trackingClickEvent(
    TRACKING_ACTION.GO_TO_VALUATION_FORM,
    TRACKING_LABEL.VALUATION_FUNNEL_TOP_BUTTON,
    true,
    {
      h_extra_information_144: canCreate ? 'property_limit_under' : 'property_limit_reached',
    },
  );

  if (canCreate) {
    push(`${VALUATION_FORM_PATH[locale.value]}`);
  } else {
    showLimitMessage.value = true;
    trackingFeatureDisplayEvent(
      TRACKING_ACTION.DISPLAY_ERROR_MESSAGE,
      TRACKING_LABEL.PROPERTY_LIMIT_REACHED,
    );
  }
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.button[class*='HgButton_button'] {
  // use max-width here to only overwrite the style on small screen
  @media screen and (max-width: calc($viewport-md - 1px)) {
    min-width: unset;
    width: $space-150;
    height: $space-150;
    padding: 0;
    background-color: transparent !important;
    border: none !important;
    > span {
      display: none;
    }
    > svg {
      color: $color-black;
    }
  }
}

.inlineContainer {
  display: flex;
  align-items: center;
  gap: $space-50; // adjust spacing as needed
}

.toastNotificationContainer {
  [class*='HgToast_toast_'] {
    @include grid-media-query(sm) {
      top: $space-500;
    }
  }
}
</style>
